/**
 * Header predictive search — open/close, debounced fetch, DOMParser swap, cache.
 *
 * JS toggles data-drawer-open on the root + body[data-header-search-open] (scroll-lock).
 * Panel visibility driven by [data-header-search]:not([data-search-state="closed"]) (~line 2059).
 * CSS drives ALL transitions + state visuals via [data-search-state] attr.
 * ponytail: no Zustand; all state is DOM-local (attrs + local vars).
 *
 * State machine on [data-header-search][data-search-state]:
 *   closed | idle | loading | results | no-results
 *
 * Fetch: /search?q=<term>&section_id=<targetId>&options[prefix]=last
 * Swap:  #PredictiveSearchResults-<targetId>.innerHTML
 *
 * Open/close:
 *   - setState('idle') on [data-header-search] → [data-search-state!=closed] reveals panel
 *   - body[data-header-search-open] scroll-lock
 */

import trapFocus from './header-focus-trap';

const DEBOUNCE_MS = 300;

/** Minimal debounce — ponytail: one closure, no library. */
function debounce(fn, ms) {
    let timer = null;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), ms);
    };
}

/**
 * Default export. Call once per DOMContentLoaded / shopify:section:load.
 * Returns { destroy } for re-init safety (mirrors initHeaderMegaMenu contract).
 * @returns {{ destroy: () => void }}
 */
export default function initHeaderSearch() {
    const root = document.querySelector('[data-header-search]');

    // Guard: no search panel in DOM (section disabled or not rendered)
    if (!root) return { destroy: () => {} };

    // Guard: predictive search disabled — triggers submit the plain <form> instead
    if (root.dataset.searchEnabled !== 'true') return { destroy: () => {} };

    const targetId = root.dataset.searchTargetId; // e.g. "header-predictive-search"
    if (!targetId) return { destroy: () => {} };

    const swapId = 'PredictiveSearchResults-' + targetId;

    const input = root.querySelector('[data-header-search-input]');
    const clearBtn = root.querySelector('[data-header-search-clear]');
    const closeBtn = root.querySelector('[data-header-search-close]');
    const overlay = root.querySelector('[data-header-search-overlay]');
    const liveRegion = root.querySelector('[data-header-search-count-live]');

    // All [data-header-search-trigger] are outside root (in the header bar)
    const triggers = [...document.querySelectorAll('[data-header-search-trigger]')];

    // Guard: no input element means the panel markup is absent — bail cleanly
    if (!input) return { destroy: () => {} };

    // ── State helpers ────────────────────────────────────────────────────────

    /** The trigger that last opened the panel — used for focus-return on close. */
    let lastTrigger = null;

    /** Active focus-trap release fn (null when closed). */
    let releaseTrap = null;

    const dialogEl = root.querySelector('.drawer__inner') || root;

    function setTriggersExpanded(expanded) {
        triggers.forEach((t) => t.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
    }

    function setState(state) {
        root.setAttribute('data-search-state', state);
        // a11y: combobox is expanded whenever the panel is open (any non-closed state)
        input.setAttribute('aria-expanded', state === 'closed' ? 'false' : 'true');
    }

    // ponytail: isOpen reads data-search-state (same selector CSS uses for panel visibility
    // at ~line 2059 of header.scss); .active is kept only for reference overlay fade.
    function isOpen() {
        return root.getAttribute('data-search-state') !== 'closed';
    }

    function openPanel(triggerEl) {
        lastTrigger = triggerEl || null;
        // Desktop panel opens below the announcement bar (Figma 13852:13270) —
        // measure its visible bottom (0 once scrolled past) at open time.
        const announcement = document.querySelector('.announcement-bar');
        const announcementBottom = announcement
            ? Math.max(0, Math.round(announcement.getBoundingClientRect().bottom))
            : 0;
        root.style.setProperty('--search-panel-top', announcementBottom + 'px');
        // Drawer contract (same as header-mobile-drawer): data-drawer-open drives
        // the shell's slide/visibility CSS; search-drawer slides from the right.
        root.setAttribute('data-drawer-open', '');
        document.body.setAttribute('data-header-search-open', ''); // scroll-lock
        setState('idle');
        setTriggersExpanded(true);
        releaseTrap = trapFocus(dialogEl); // Tab stays inside while the page is scroll-locked
        input.focus();
    }

    function closePanel() {
        // fresh start next open — don't retain the previous query
        input.value = '';
        if (clearBtn) clearBtn.setAttribute('hidden', '');
        root.removeAttribute('data-drawer-open');
        document.body.removeAttribute('data-header-search-open'); // release scroll-lock
        setState('closed');
        setTriggersExpanded(false);
        releaseTrap?.();
        releaseTrap = null;
        lastTrigger?.focus();
        lastTrigger = null;
    }

    // ── Cache + fetch ────────────────────────────────────────────────────────

    /** ponytail: plain Map keyed by full URL — no extra abstraction needed. */
    const cachedMap = new Map();

    function buildUrl(term) {
        // Storefront /search — the SAME engine as the results page, so the panel
        // and VIEW ALL agree by construction (AC-outlet parity approach).
        // Trailing * = prefix match (the /search route ignores options[prefix]);
        // _fd=0 stops the myshopify→primary-domain redirect on preview.
        const wildcard = term.endsWith('*') ? term : `${term}*`;
        return `/search?q=${encodeURIComponent(wildcard)}&options%5Bprefix%5D=last&section_id=${encodeURIComponent(targetId)}&_fd=0`;
    }

    // Collections rail — /search never returns collections, so a secondary
    // predictive-API fetch fills [data-collections-slot] after each swap.
    const collectionsCache = new Map();

    function fillCollections(term) {
        const slot = document.querySelector('[data-collections-slot]');
        const list = document.querySelector('[data-collections-list]');
        if (!slot || !list) return;

        const render = (collections) => {
            // stale-response guard: only render for the current query
            if (input.value.trim() !== term) return;
            if (!collections.length) {
                slot.setAttribute('hidden', '');
                return;
            }
            list.innerHTML = '';
            collections.slice(0, 3).forEach((c, i) => {
                const li = document.createElement('li');
                li.setAttribute('role', 'option');
                li.setAttribute('aria-selected', 'false');
                li.id = `predictive-search-option-collection-${i + 1}`;
                const a = document.createElement('a');
                a.href = c.url;
                a.className = 'predictive-search-results__link';
                a.textContent = c.title;
                li.appendChild(a);
                list.appendChild(li);
            });
            slot.removeAttribute('hidden');
        };

        if (collectionsCache.has(term)) {
            render(collectionsCache.get(term));
            return;
        }

        fetch(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=collection&resources[limit]=3&_fd=0`)
            .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
            .then((json) => {
                const collections = json?.resources?.results?.collections || [];
                collectionsCache.set(term, collections);
                render(collections);
            })
            .catch(() => { slot.setAttribute('hidden', ''); });
    }

    function applyResults(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.getElementById(swapId);
        const target = document.getElementById(swapId);

        if (!source) {
            // Fetched fragment missing the swap container — keep prior content, log
            console.error(`[header-search] Swap source #${swapId} not found in response. Check section_id="${targetId}" resolves correctly.`);
            setState('idle');
            return;
        }
        if (!target) {
            console.error(`[header-search] Swap target #${swapId} not found in document.`);
            setState('idle');
            return;
        }

        target.innerHTML = source.innerHTML;
        // innerHTML swap replaces children only — the outer div's data-results-count
        // stays at its server-rendered "0". Read the count from the fetched fragment.
        target.dataset.resultsCount = source.dataset.resultsCount || '0';

        // Determine results vs no-results from Liquid-stamped data attr
        const count = parseInt(source.dataset.resultsCount, 10);
        setState(count > 0 ? 'results' : 'no-results');

        if (count > 0) fillCollections(input.value.trim());

        // a11y live region — announce count to screen readers
        if (liveRegion) {
            liveRegion.textContent = count > 0
                ? `${count} result${count === 1 ? '' : 's'} found`
                : 'No results found';
        }
    }

    function fetchResults(term) {
        const url = buildUrl(term);

        if (cachedMap.has(url)) {
            applyResults(cachedMap.get(url));
            return;
        }

        setState('loading');

        fetch(url)
            .then((res) => {
                if (!res.ok) throw new Error(`[header-search] fetch failed: ${res.status}`);
                return res.text();
            })
            .then((html) => {
                cachedMap.set(url, html);
                applyResults(html);
            })
            .catch((err) => {
                console.error(err);
                // Never leave panel stuck on loading — back to idle on any error
                setState('idle');
            });
    }

    // ── Input handler (debounced) ─────────────────────────────────────────────

    const onInput = debounce(() => {
        const term = input.value.trim();

        // Show/hide clear button based on value presence
        if (clearBtn) {
            if (term.length > 0) {
                clearBtn.removeAttribute('hidden');
            } else {
                clearBtn.setAttribute('hidden', '');
            }
        }

        if (term.length === 0) {
            setState('idle');
            return;
        }

        fetchResults(term);
    }, DEBOUNCE_MS);

    // ── Clear ────────────────────────────────────────────────────────────────

    function onClear() {
        // desktop pill ✕ is always visible — with nothing to clear it closes the panel
        if (input.value === '') {
            closePanel();
            return;
        }
        input.value = '';
        if (clearBtn) clearBtn.setAttribute('hidden', '');
        setState('idle');
        input.focus();
    }

    // ── Keyboard (Escape) ────────────────────────────────────────────────────

    function onKeydown(e) {
        if (e.key !== 'Escape') return;
        if (!isOpen()) return;
        e.preventDefault();
        closePanel();
    }

    // ── Outside-click (desktop dropdown) ─────────────────────────────────────

    function onDocClick(e) {
        if (!isOpen()) return;
        // Click inside root or on a trigger → keep open
        if (root.contains(e.target)) return;
        if (triggers.some((t) => t.contains(e.target))) return;
        closePanel();
    }

    // ── Listener registry ─────────────────────────────────────────────────────

    const listeners = [];
    function addListener(el, type, fn, opts) {
        if (!el) return;
        el.addEventListener(type, fn, opts);
        listeners.push({ el, type, fn, opts });
    }

    // ── Bind ──────────────────────────────────────────────────────────────────

    triggers.forEach((trigger) => {
        addListener(trigger, 'click', () => openPanel(trigger));
    });

    // Click on the panel wrapper (dimmed area outside the dialog) closes
    const panelWrap = root.querySelector('.burger-drawer__panel');
    if (panelWrap) {
        addListener(panelWrap, 'click', (e) => {
            if (e.target === panelWrap) closePanel();
        });
    }

    // Trending pills search in place — no page navigation (href stays for no-JS)
    addListener(root, 'click', (e) => {
        const pill = e.target.closest('.search-drawer__trending-pill');
        if (!pill) return;
        e.preventDefault();
        input.value = pill.textContent.trim();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
    });

    // Enter submits to /search — wildcard the term so the page prefix-matches
    // like the predictive panel (the /search route ignores options[prefix])
    const form = input.closest('form');
    if (form) {
        addListener(form, 'submit', () => {
            const term = input.value.trim();
            if (term && !term.endsWith('*')) input.value = term + '*';
        });
    }

    addListener(input, 'input', onInput);
    addListener(clearBtn, 'click', onClear);
    addListener(closeBtn, 'click', closePanel);
    addListener(overlay, 'click', closePanel);

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onDocClick);

    // ── destroy() ─────────────────────────────────────────────────────────────

    function destroy() {
        listeners.forEach(({ el, type, fn, opts }) => el.removeEventListener(type, fn, opts));
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('click', onDocClick);
        // Always clean up body scroll-lock attr regardless of isOpen() to be safe
        if (isOpen()) closePanel();
        releaseTrap?.();
        releaseTrap = null;
        document.body.removeAttribute('data-header-search-open');
    }

    return { destroy };
}
